import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { 
  Shield, LogOut, Users, Search, Image, Heart, Share2, 
  DollarSign, TrendingUp, Trash2, Plus, Save, ArrowLeft,
  Edit2, X, MousePointer, ExternalLink, ChevronLeft, ChevronRight,
  User, Calendar, Activity, Ban, Check
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_69566509-b9c0-48ae-b149-6b9accd67eee/artifacts/3nmi4pqp_Dynamic%20Logo%20with%20Sapphire%20Coral%20Gradient%20%288%29.png";

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [affiliateConfigs, setAffiliateConfigs] = useState([]);
  const [availableNetworks, setAvailableNetworks] = useState({});
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [savingNetwork, setSavingNetwork] = useState(null);
  const [newConfig, setNewConfig] = useState({ network: "", affiliate_id: "", is_active: true });
  const [activeSection, setActiveSection] = useState('overview');
  const [editingConfig, setEditingConfig] = useState(null);
  const [editValue, setEditValue] = useState("");
  
  // User management state
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    verifyAdmin();
  }, []);

  const verifyAdmin = async () => {
    try {
      await axios.get(`${API}/admin/verify`, { withCredentials: true });
      setIsAuthenticated(true);
      loadData();
    } catch {
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      await axios.post(`${API}/admin/login`, { username, password }, { withCredentials: true });
      setIsAuthenticated(true);
      loadData();
      toast.success("Welcome back");
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
      setIsAuthenticated(false);
      navigate("/admin");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const loadData = async () => {
    setIsLoadingMetrics(true);
    try {
      const [metricsRes, affiliatesRes] = await Promise.all([
        axios.get(`${API}/admin/metrics`, { withCredentials: true }),
        axios.get(`${API}/admin/affiliate-configs`, { withCredentials: true })
      ]);
      setMetrics(metricsRes.data);
      setAffiliateConfigs(affiliatesRes.data.configs || []);
      setAvailableNetworks(affiliatesRes.data.available_networks || {});
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setIsLoadingMetrics(false);
    }
  };

  const loadUsers = async (page = 1, search = "") => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/admin/users`, {
        params: { page, limit: 10, search },
        withCredentials: true
      });
      setUsers(res.data.users || []);
      setUsersTotal(res.data.total || 0);
      setUsersPage(page);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, { withCredentials: true });
      setUserDetail(res.data);
      setSelectedUser(userId);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const toggleUserStatus = async (userId, isSuspended) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, 
        { is_suspended: isSuspended },
        { withCredentials: true }
      );
      toast.success(isSuspended ? "User suspended" : "User activated");
      loadUsers(usersPage, usersSearch);
      if (userDetail && userDetail.user.user_id === userId) {
        loadUserDetail(userId);
      }
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const saveAffiliateConfig = async (config) => {
    setSavingNetwork(config.network);
    try {
      await axios.post(`${API}/admin/affiliate-configs`, config, { withCredentials: true });
      toast.success(`Configuration saved`);
      loadData();
      setEditingConfig(null);
      setEditValue("");
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSavingNetwork(null);
    }
  };

  const addNewConfig = async () => {
    if (!newConfig.network || !newConfig.affiliate_id) {
      toast.error("Please fill in all fields");
      return;
    }
    await saveAffiliateConfig(newConfig);
    setNewConfig({ network: "", affiliate_id: "", is_active: true });
  };

  const deleteConfig = async (network) => {
    try {
      await axios.delete(`${API}/admin/affiliate-configs/${network}`, { withCredentials: true });
      toast.success("Configuration removed");
      loadData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const updateConfigStatus = async (network, isActive) => {
    const config = affiliateConfigs.find(c => c.network === network);
    if (config) {
      await saveAffiliateConfig({ ...config, is_active: isActive });
    }
  };

  const startEditConfig = (config) => {
    setEditingConfig(config.network);
    setEditValue(config.affiliate_id);
  };

  const saveEditConfig = async (network) => {
    const config = affiliateConfigs.find(c => c.network === network);
    if (config && editValue.trim()) {
      await saveAffiliateConfig({ ...config, affiliate_id: editValue.trim() });
    }
  };

  const cancelEdit = () => {
    setEditingConfig(null);
    setEditValue("");
  };

  // Load users when switching to users tab
  useEffect(() => {
    if (activeSection === 'users' && users.length === 0) {
      loadUsers();
    }
  }, [activeSection]);

  // Login Screen
  if (isAuthenticated === false) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-6" 
        style={{ backgroundColor: 'var(--background-primary)' }}
        data-testid="admin-login"
      >
        <div className="w-full max-w-sm animate-fade-in">
          {/* Logo */}
          <div className="text-center mb-12">
            <Link to="/">
              <img src={LOGO_URL} alt="Shadesense.ai" className="h-12 w-auto mx-auto mb-10" />
            </Link>
            <div className="flex items-center justify-center gap-2 mb-4">
              <Shield className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
              <span className="text-meta uppercase tracking-widest" style={{ color: 'var(--accent-primary)' }}>Admin</span>
            </div>
            <h1 className="text-h2 mb-3" style={{ color: 'var(--text-primary)' }}>
              Control Panel
            </h1>
            <p className="text-small text-secondary">
              Sign in to manage your platform
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="card-glass p-8">
            <div className="space-y-4 mb-6">
              <div>
                <label className="text-meta text-muted uppercase tracking-widest block mb-2">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input"
                  placeholder="Enter username"
                  data-testid="admin-username"
                />
              </div>
              <div>
                <label className="text-meta text-muted uppercase tracking-widest block mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                  placeholder="Enter password"
                  data-testid="admin-password"
                />
              </div>
            </div>
            <button 
              type="submit" 
              disabled={isLoggingIn} 
              className="btn-primary w-full"
              data-testid="admin-login-btn"
            >
              {isLoggingIn ? (
                <span className="animate-shimmer w-16 h-4 rounded inline-block"></span>
              ) : (
                'Continue'
              )}
            </button>
          </form>

          {/* Back Link */}
          <div className="mt-8 text-center">
            <Link to="/" className="text-small text-secondary hover:text-primary transition-colors">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Loading
  if (isAuthenticated === null || isLoadingMetrics) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-primary)' }}>
        <div className="animate-shimmer w-32 h-1 rounded-full"></div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-primary)' }} data-testid="admin-dashboard">
      {/* Header */}
      <header className="nav">
        <div className="nav-content">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Shadesense.ai" className="h-10 w-auto" />
            <span className="text-meta uppercase tracking-widest" style={{ color: 'var(--accent-primary)' }}>Admin</span>
          </Link>
          <button onClick={handleLogout} className="btn-secondary flex items-center gap-2">
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-12">
        <div className="max-w-6xl mx-auto">
          
          {/* Page Header */}
          <div className="mb-12">
            <h1 className="text-display mb-4" style={{ color: 'var(--text-primary)' }}>
              Dashboard
            </h1>
            <p className="text-body text-secondary">
              Monitor platform activity, manage affiliates, and user accounts.
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="tab-list mb-12">
            <button 
              onClick={() => setActiveSection('overview')} 
              className={`tab ${activeSection === 'overview' ? 'active' : ''}`}
            >
              Overview
            </button>
            <button 
              onClick={() => setActiveSection('clicks')} 
              className={`tab ${activeSection === 'clicks' ? 'active' : ''}`}
            >
              Click Analytics
            </button>
            <button 
              onClick={() => setActiveSection('affiliates')} 
              className={`tab ${activeSection === 'affiliates' ? 'active' : ''}`}
            >
              Affiliates
            </button>
            <button 
              onClick={() => setActiveSection('users')} 
              className={`tab ${activeSection === 'users' ? 'active' : ''}`}
            >
              Users
            </button>
          </div>

          {/* Overview Section */}
          {activeSection === 'overview' && (
            <div className="animate-fade-in">
              {/* Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
                {[
                  { label: "Total Users", value: metrics?.overview?.total_users || 0, icon: Users },
                  { label: "Analyses", value: metrics?.overview?.total_analyses || 0, icon: Image },
                  { label: "Searches", value: metrics?.overview?.total_searches || 0, icon: Search },
                  { label: "Wishlist Items", value: metrics?.overview?.total_wishlist_items || 0, icon: Heart },
                  { label: "Shares", value: metrics?.overview?.total_shares || 0, icon: Share2 },
                  { label: "Affiliate Clicks", value: metrics?.overview?.total_affiliate_clicks || 0, icon: MousePointer }
                ].map((stat, index) => (
                  <div 
                    key={stat.label} 
                    className="card text-center"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`metric-${stat.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <stat.icon className="w-5 h-5 mx-auto mb-3" style={{ color: 'var(--accent-primary)' }} />
                    <p className="text-h2 mb-1" style={{ color: 'var(--text-primary)' }}>{stat.value}</p>
                    <p className="text-meta text-muted uppercase tracking-wider">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Recent Activity */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    <TrendingUp className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                    <span className="text-small text-secondary">New Users (7 days)</span>
                  </div>
                  <p className="text-display" style={{ color: 'var(--text-primary)' }}>
                    {metrics?.recent_activity?.new_users_7d || 0}
                  </p>
                </div>
                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    <Image className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                    <span className="text-small text-secondary">Analyses (7 days)</span>
                  </div>
                  <p className="text-display" style={{ color: 'var(--text-primary)' }}>
                    {metrics?.recent_activity?.analyses_7d || 0}
                  </p>
                </div>
                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    <MousePointer className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                    <span className="text-small text-secondary">Clicks (7 days)</span>
                  </div>
                  <p className="text-display" style={{ color: 'var(--text-primary)' }}>
                    {metrics?.recent_activity?.affiliate_clicks_7d || 0}
                  </p>
                </div>
              </div>

              {/* Clicks by Source */}
              {metrics?.clicks_by_source?.length > 0 && (
                <div className="card">
                  <h3 className="text-h3 mb-6" style={{ color: 'var(--text-primary)' }}>Clicks by Retailer</h3>
                  <div className="space-y-3">
                    {metrics.clicks_by_source.map((item, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg"
                        style={{ backgroundColor: 'var(--surface-secondary)' }}
                      >
                        <span className="text-small" style={{ color: 'var(--text-primary)' }}>{item.source}</span>
                        <span className="text-small font-medium" style={{ color: 'var(--accent-primary)' }}>{item.count} clicks</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Click Analytics Section */}
          {activeSection === 'clicks' && (
            <div className="animate-fade-in">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Product Click-throughs */}
                <div className="card">
                  <h3 className="text-h3 mb-6" style={{ color: 'var(--text-primary)' }}>
                    <MousePointer className="w-5 h-5 inline-block mr-2" style={{ color: 'var(--accent-primary)' }} />
                    Product Click-Throughs
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {metrics?.product_clicks?.length > 0 ? (
                      metrics.product_clicks.map((item, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between p-3 rounded-lg"
                          style={{ backgroundColor: 'var(--surface-secondary)' }}
                        >
                          <div className="flex-1 min-w-0 mr-3">
                            <p className="text-small truncate" style={{ color: 'var(--text-primary)' }}>
                              {item.title || 'Unknown Product'}
                            </p>
                            {item.url && (
                              <a 
                                href={item.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-meta text-muted hover:text-accent-primary flex items-center gap-1"
                              >
                                View <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          <span className="text-small font-medium px-2 py-1 rounded" style={{ backgroundColor: 'var(--accent-muted)', color: 'var(--accent-primary)' }}>
                            {item.count}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-small text-muted text-center py-8">No product clicks recorded yet</p>
                    )}
                  </div>
                </div>

                {/* Daily Clicks Chart */}
                <div className="card">
                  <h3 className="text-h3 mb-6" style={{ color: 'var(--text-primary)' }}>
                    <TrendingUp className="w-5 h-5 inline-block mr-2" style={{ color: 'var(--accent-primary)' }} />
                    Daily Clicks (30 days)
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {metrics?.daily_clicks?.length > 0 ? (
                      metrics.daily_clicks.map((item, index) => {
                        const maxCount = Math.max(...metrics.daily_clicks.map(d => d.count));
                        const percentage = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                        return (
                          <div key={index} className="flex items-center gap-3">
                            <span className="text-meta text-muted w-20">{item.date?.slice(5) || '-'}</span>
                            <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                              <div 
                                className="h-full rounded-full transition-all"
                                style={{ 
                                  width: `${percentage}%`,
                                  backgroundColor: 'var(--accent-primary)'
                                }}
                              />
                            </div>
                            <span className="text-small font-medium w-8 text-right" style={{ color: 'var(--text-primary)' }}>{item.count}</span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-small text-muted text-center py-8">No daily data available</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Clicks by Source */}
              <div className="card">
                <h3 className="text-h3 mb-6" style={{ color: 'var(--text-primary)' }}>
                  <DollarSign className="w-5 h-5 inline-block mr-2" style={{ color: 'var(--accent-primary)' }} />
                  Clicks by Retailer
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {metrics?.clicks_by_source?.length > 0 ? (
                    metrics.clicks_by_source.map((item, index) => (
                      <div 
                        key={index}
                        className="text-center p-4 rounded-lg"
                        style={{ backgroundColor: 'var(--surface-secondary)' }}
                      >
                        <p className="text-h2 mb-1" style={{ color: 'var(--accent-primary)' }}>{item.count}</p>
                        <p className="text-meta text-muted truncate">{item.source}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-small text-muted col-span-full text-center py-8">No clicks recorded yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Affiliates Section */}
          {activeSection === 'affiliates' && (
            <div className="animate-fade-in">
              <div className="card mb-8">
                <h3 className="text-h3 mb-2" style={{ color: 'var(--text-primary)' }}>Affiliate Configuration</h3>
                <p className="text-small text-secondary mb-8">
                  Configure your affiliate IDs for each network. Links will automatically include your affiliate parameters.
                </p>
                
                {/* Existing Configs */}
                <div className="space-y-4 mb-8">
                  {affiliateConfigs.map((config) => (
                    <div 
                      key={config.network} 
                      className="flex items-center gap-4 p-4 rounded-lg"
                      style={{ backgroundColor: 'var(--surface-secondary)' }}
                      data-testid={`affiliate-config-${config.network}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                          <span className="text-body font-medium" style={{ color: 'var(--text-primary)' }}>
                            {availableNetworks[config.network]?.name || config.network}
                          </span>
                        </div>
                        {editingConfig === config.network ? (
                          <div className="flex items-center gap-2 mt-2">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="input flex-1"
                              placeholder="Enter affiliate ID"
                              autoFocus
                            />
                            <button 
                              onClick={() => saveEditConfig(config.network)}
                              className="p-2 rounded-lg hover:bg-green-50 transition-colors"
                              disabled={savingNetwork === config.network}
                            >
                              <Check className="w-4 h-4" style={{ color: 'green' }} />
                            </button>
                            <button 
                              onClick={cancelEdit}
                              className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              <X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                            </button>
                          </div>
                        ) : (
                          <p className="text-meta text-muted font-mono">ID: {config.affiliate_id}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        {editingConfig !== config.network && (
                          <button 
                            onClick={() => startEditConfig(config)}
                            className="p-2 rounded-lg hover:bg-blue-50 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                          </button>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="text-meta text-muted">Active</span>
                          <Switch
                            checked={config.is_active}
                            onCheckedChange={(checked) => updateConfigStatus(config.network, checked)}
                            data-testid={`toggle-${config.network}`}
                          />
                        </div>
                        <button 
                          onClick={() => deleteConfig(config.network)} 
                          className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {affiliateConfigs.length === 0 && (
                    <p className="text-small text-muted text-center py-8">No affiliate configurations yet. Add one below.</p>
                  )}
                </div>

                {/* Add New Config */}
                <div className="pt-6" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <h4 className="text-small uppercase tracking-widest mb-4 flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                    <Plus className="w-4 h-4" />
                    Add New Affiliate
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-meta text-muted block mb-2">Network</label>
                      <select
                        value={newConfig.network}
                        onChange={(e) => setNewConfig({ ...newConfig, network: e.target.value })}
                        className="input"
                        data-testid="new-affiliate-network"
                      >
                        <option value="">Select network...</option>
                        {Object.entries(availableNetworks)
                          .filter(([key]) => !affiliateConfigs.find(c => c.network === key))
                          .map(([key, val]) => (
                            <option key={key} value={key}>{val.name}</option>
                          ))
                        }
                      </select>
                    </div>
                    <div>
                      <label className="text-meta text-muted block mb-2">Affiliate ID</label>
                      <input
                        type="text"
                        value={newConfig.affiliate_id}
                        onChange={(e) => setNewConfig({ ...newConfig, affiliate_id: e.target.value })}
                        className="input"
                        placeholder="Enter your affiliate ID"
                        data-testid="new-affiliate-id"
                      />
                    </div>
                    <div className="flex items-end">
                      <button 
                        onClick={addNewConfig} 
                        className="btn-primary w-full" 
                        data-testid="add-affiliate-btn"
                        disabled={!newConfig.network || !newConfig.affiliate_id}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </button>
                    </div>
                  </div>
                </div>

                {/* Supported Networks */}
                <div className="mt-8 p-4 rounded-lg" style={{ backgroundColor: 'var(--accent-muted)' }}>
                  <h4 className="text-meta uppercase tracking-widest mb-3" style={{ color: 'var(--accent-primary)' }}>Supported Networks</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {Object.entries(availableNetworks).map(([key, val]) => (
                      <div key={key} className="text-small">
                        <span style={{ color: 'var(--text-primary)' }}>{val.name}</span>
                        <span className="text-muted ml-1">({val.param})</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Users Section */}
          {activeSection === 'users' && (
            <div className="animate-fade-in">
              {selectedUser && userDetail ? (
                // User Detail View
                <div>
                  <button 
                    onClick={() => { setSelectedUser(null); setUserDetail(null); }}
                    className="flex items-center gap-2 text-small text-secondary hover:text-primary mb-6 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back to Users
                  </button>
                  
                  <div className="card mb-6">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center gap-4">
                        {userDetail.user.picture ? (
                          <img 
                            src={userDetail.user.picture} 
                            alt={userDetail.user.name} 
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--accent-muted)' }}>
                            <User className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
                          </div>
                        )}
                        <div>
                          <h2 className="text-h2" style={{ color: 'var(--text-primary)' }}>{userDetail.user.name}</h2>
                          <p className="text-small text-muted">{userDetail.user.email}</p>
                          <p className="text-meta text-muted mt-1">
                            <Calendar className="w-3 h-3 inline-block mr-1" />
                            Joined {new Date(userDetail.user.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleUserStatus(userDetail.user.user_id, !userDetail.user.is_suspended)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          userDetail.user.is_suspended 
                            ? 'bg-green-50 text-green-600 hover:bg-green-100' 
                            : 'bg-red-50 text-red-600 hover:bg-red-100'
                        }`}
                      >
                        {userDetail.user.is_suspended ? (
                          <>
                            <Check className="w-4 h-4" />
                            Activate
                          </>
                        ) : (
                          <>
                            <Ban className="w-4 h-4" />
                            Suspend
                          </>
                        )}
                      </button>
                    </div>

                    {/* User Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                        <p className="text-h2" style={{ color: 'var(--accent-primary)' }}>{userDetail.stats.total_analyses}</p>
                        <p className="text-meta text-muted">Analyses</p>
                      </div>
                      <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                        <p className="text-h2" style={{ color: 'var(--accent-primary)' }}>{userDetail.stats.total_wishlist}</p>
                        <p className="text-meta text-muted">Wishlist</p>
                      </div>
                      <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                        <p className="text-h2" style={{ color: 'var(--accent-primary)' }}>{userDetail.stats.total_clicks}</p>
                        <p className="text-meta text-muted">Clicks</p>
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <h3 className="text-small uppercase tracking-widest mb-4" style={{ color: 'var(--text-secondary)' }}>
                      Recent Activity
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {userDetail.recent_events?.length > 0 ? (
                        userDetail.recent_events.map((event, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-3 rounded-lg"
                            style={{ backgroundColor: 'var(--surface-secondary)' }}
                          >
                            <div className="flex items-center gap-2">
                              <Activity className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                              <span className="text-small" style={{ color: 'var(--text-primary)' }}>{event.event_type}</span>
                            </div>
                            <span className="text-meta text-muted">{new Date(event.created_at).toLocaleString()}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-small text-muted text-center py-4">No recent activity</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // Users List View
                <div className="card">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-h3" style={{ color: 'var(--text-primary)' }}>
                      <Users className="w-5 h-5 inline-block mr-2" style={{ color: 'var(--accent-primary)' }} />
                      User Accounts ({usersTotal})
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                        <input
                          type="text"
                          value={usersSearch}
                          onChange={(e) => setUsersSearch(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && loadUsers(1, usersSearch)}
                          className="input pl-10"
                          placeholder="Search users..."
                          style={{ width: '200px' }}
                        />
                      </div>
                      <button 
                        onClick={() => loadUsers(1, usersSearch)}
                        className="btn-secondary"
                      >
                        Search
                      </button>
                    </div>
                  </div>

                  {loadingUsers ? (
                    <div className="text-center py-12">
                      <div className="animate-shimmer w-32 h-1 rounded-full mx-auto"></div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3 mb-6">
                        {users.length > 0 ? (
                          users.map((user) => (
                            <div 
                              key={user.user_id}
                              className="flex items-center justify-between p-4 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                              style={{ backgroundColor: 'var(--surface-secondary)' }}
                              onClick={() => loadUserDetail(user.user_id)}
                            >
                              <div className="flex items-center gap-4">
                                {user.picture ? (
                                  <img 
                                    src={user.picture} 
                                    alt={user.name} 
                                    className="w-10 h-10 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--accent-muted)' }}>
                                    <User className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                                  </div>
                                )}
                                <div>
                                  <p className="text-body font-medium" style={{ color: 'var(--text-primary)' }}>
                                    {user.name}
                                    {user.is_suspended && (
                                      <span className="ml-2 text-meta px-2 py-0.5 rounded bg-red-100 text-red-600">Suspended</span>
                                    )}
                                  </p>
                                  <p className="text-small text-muted">{user.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-6 text-right">
                                <div>
                                  <p className="text-small" style={{ color: 'var(--text-primary)' }}>{user.analyses_count} analyses</p>
                                  <p className="text-meta text-muted">{user.wishlist_count} wishlist</p>
                                </div>
                                <div>
                                  <p className="text-meta text-muted">Last active</p>
                                  <p className="text-small" style={{ color: 'var(--text-secondary)' }}>
                                    {user.last_active ? new Date(user.last_active).toLocaleDateString() : '-'}
                                  </p>
                                </div>
                                <ChevronRight className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-small text-muted text-center py-8">No users found</p>
                        )}
                      </div>

                      {/* Pagination */}
                      {usersTotal > 10 && (
                        <div className="flex items-center justify-center gap-4">
                          <button
                            onClick={() => loadUsers(usersPage - 1, usersSearch)}
                            disabled={usersPage <= 1}
                            className="btn-secondary flex items-center gap-1"
                          >
                            <ChevronLeft className="w-4 h-4" />
                            Previous
                          </button>
                          <span className="text-small text-muted">
                            Page {usersPage} of {Math.ceil(usersTotal / 10)}
                          </span>
                          <button
                            onClick={() => loadUsers(usersPage + 1, usersSearch)}
                            disabled={usersPage >= Math.ceil(usersTotal / 10)}
                            className="btn-secondary flex items-center gap-1"
                          >
                            Next
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p className="text-meta text-muted">
            Shadesense.ai Admin Panel
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Admin;
